import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, CUSTOM_ELEMENTS_SCHEMA, enableProdMode, NgModule } from "@angular/core";
import { BrowserModule, Meta } from "@angular/platform-browser";

import { OKTA_CONFIG, OktaAuthModule } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { Router } from "@angular/router";

import { environment } from "@environments/environment";
import {
  ENVIRONMENT_CONFIG,
  HttpOktaService,
  HttpSiteminderService,
  KongInterceptor,
  UrlService,
  WindowRef,
  sessionAuthorizationFactory,
  GATEKEEPER_CONFIG,
} from "@weps/shared-http";
import { HttpInterceptors } from "@services/http-interceptor";
import { CookieFilterInterceptor } from "./interceptors/cookie-filter.interceptor";
import { HttpLoggingInterceptor } from "./interceptors/http-logging.interceptor";
import { UtilityService } from "@services/utility.service";
import { VexService } from "@services/vex/vex.service";
import { Permissions } from "@services/authentication/auth0guard";
import { RknAuthGuard } from "@weps/shared-http";
import { AppComponent } from "./app.component";
import { AppRoutingModule } from "./app-routing.module";
import { UnauthorizedComponent } from "@weps/shared-ui";
import { LinkModule } from "@vg-constellation/angular-17/link";
import { ModalDialogModule } from "@vg-constellation/angular-17/modal-dialog";

const oktaAuth = new OktaAuth(environment.oidc);
const isInternal: boolean = environment.internal;

const externalProviders = [
  {
    provide: ENVIRONMENT_CONFIG,
    useValue: environment,
  },
  {
    provide: APP_INITIALIZER,
    multi: true,
    useFactory: sessionAuthorizationFactory,
    deps: [HttpClient, WindowRef, Meta, ENVIRONMENT_CONFIG, UrlService],
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: KongInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpLoggingInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: CookieFilterInterceptor,
    multi: true,
  },
  {
    provide: "IHttpService",
    useClass: HttpSiteminderService,
  },
  {
    provide: GATEKEEPER_CONFIG,
    useValue: {
      featureIds: "ENABLE_TRUST_AS_OWNER,ENABLE_JOINT_AS_OWNER,ENABLE_BENEFICIARIES",
      gatekeeperEnv: environment.gatekeeper_env,
    },
  },
  UrlService,
  VexService,
  UtilityService,
  WindowRef,
  Permissions,
];

const internalProviders = [
  {
    provide: ENVIRONMENT_CONFIG,
    useValue: environment,
  },
  {
    provide: OKTA_CONFIG,
    useFactory: (router: Router) => ({
      oktaAuth,
      restoreOriginalUri: (_: OktaAuth, originalUri: string) => {
        return router.navigateByUrl(originalUri || "/personal-wealth-inventory");
      },
    }),
    deps: [Router],
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: KongInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpLoggingInterceptor,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: HttpInterceptors,
    multi: true,
  },
  {
    provide: HTTP_INTERCEPTORS,
    useClass: CookieFilterInterceptor,
    multi: true,
  },
  {
    provide: "IHttpService",
    useClass: HttpOktaService,
  },
  {
    provide: GATEKEEPER_CONFIG,
    useValue: {
      featureIds: "ENABLE_TRUST_AS_OWNER,ENABLE_JOINT_AS_OWNER,ENABLE_BENEFICIARIES",
      gatekeeperEnv: environment.gatekeeper_env,
    },
  },
  UrlService,
  VexService,
  UtilityService,
  RknAuthGuard,
];

const externalModules = [
  BrowserModule,
  HttpClientModule,
  AppRoutingModule,
  LinkModule,
  ModalDialogModule,
];

const internalModules = [
  BrowserModule,
  HttpClientModule,
  AppRoutingModule,
  LinkModule,
  ModalDialogModule,
  OktaAuthModule,
];

if (environment.production) {
  enableProdMode();
}

@NgModule({
  declarations: [AppComponent],
  imports: [...(isInternal ? internalModules : externalModules), UnauthorizedComponent],
  providers: isInternal ? internalProviders : externalProviders,
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
